<template>
    <div :class="classList">
        <div v-if="isPageReady && !invitedUserEmail">
            <common-page-header
                :heading="showAcceptingInsurance ? $t('pages.profile.professional.availability.choose') : getPageHeader"
                :content="
                    showNotAcceptingInsurance
                        ? selectedUser.designation
                            ? `${selectedUser.name}, ${selectedUser.designation}`
                            : selectedUser.name
                        : ''
                "
                :isCenter="true"
                :contentSpacing="showAcceptingInsurance ? 'medium' : ''"
                size="minor"
            ></common-page-header>

            <common-page-header
                v-if="showNotAcceptingInsurance"
                :content="$t('modals.make_direct_referal_modal.content')"
                :isCenter="true"
                size="minor"
            ></common-page-header>

            <common-page-header
                v-if="showNotAcceptingInsurance"
                :content="$t('modals.make_direct_referal_modal.insurance_plans', { insurance: insurance.name })"
                :isCenter="true"
                size="minor"
            ></common-page-header>

            <common-content-spacer
                v-show="showNotAcceptingInsurance"
                class="direct-referral__no-insurance-container"
                height="medium"
            >
                <common-divider class="direct-referral__header"></common-divider>
                <p class="direct-referral__title">
                    {{ $t('modals.make_direct_referal_modal.buttons.find_provider') }}
                </p>

                <common-buttons>
                    <common-button
                        :label="$t('modals.make_direct_referal_modal.buttons.match_referral')"
                        size="large"
                        type="primary"
                        @click="matchReferral()"
                    ></common-button>
                </common-buttons>
            </common-content-spacer>

            <common-content-spacer v-if="showAcceptingInsurance" height="medium">
                <user-card
                    v-if="practiceTypeValue === PracticeTypeEnum.PHYSICIAN"
                    :physician="selectedUser"
                    size="medium"
                ></user-card>

                <practice-card
                    v-if="practiceTypeValue === PracticeTypeEnum.ANCILLARY"
                    :practice="selectedUser"
                    size="medium"
                ></practice-card>

                <div>
                    <common-divider>
                        <p class="direct-referral__check_availability_header">
                            {{ $t('modals.make_direct_referal_modal.availability') }}
                        </p>
                    </common-divider>

                    <availability-locations
                        v-if="practiceLocations.length"
                        :hoverTitle="$t('pages.profile.professional.availability.choose')"
                        :userId="selectedUser.id"
                        :maxNumberOfItems="maxNumberOfItems"
                        :locations="practiceLocations"
                        @go-to-location="eprescribe ? openTestsSelection($event) : goToSchedule($event)"
                    ></availability-locations>
                </div>
            </common-content-spacer>

            <common-content-spacer
                v-show="!showAcceptingInsurance && !showNotAcceptingInsurance"
                height="medium"
                class="refer-direct-container"
            >
                <div class="direct-referral__inputs">
                    <common-user-search-autocomplete
                        ref="searchAC"
                        class="direct-referral__input"
                        :label="searchLabel"
                        :placeholder="searchPlaceholder"
                        :user="user"
                        :practiceType="practiceTypeValue"
                        :isDisabled="isSending"
                        :searchRegistry="true"
                        @user-select="userSelected($event)"
                        @input-change="removeSearchTerm"
                        @no-results="setSearchTerm"
                    ></common-user-search-autocomplete>

                    <div v-if="practiceOptions.length > 1" class="direct-referral__insurance">
                        <label class="form-field__label">
                            {{
                                $t('elements.referrals.provider_search_card.practice_choice', {
                                    count: practiceOptions.length,
                                })
                            }}
                        </label>
                        <select-input
                            class="direct-referral__input"
                            :isDisabled="isSending"
                            :options="practiceOptions"
                            :value="selectedUser && selectedUser.id"
                            @change="choosePractice(Number($event))"
                        ></select-input>
                    </div>

                    <div class="direct-referral__insurance">
                        <label class="form-field__label">
                            {{ $t('forms.auth.practice.fields.insurance_plans.placeholder_patient') }}
                        </label>
                        <select-input
                            class="direct-referral__input"
                            :isDisabled="isSending"
                            :placeholder="$t('forms.auth.practice.fields.insurance_plans.start_typing')"
                            :options="insurancePlans"
                            :groupOptions="true"
                            :expandOptionsRight="true"
                            :isSearchable="true"
                            :value="insuranceId ?? patientPrimaryInsuranceTypeId"
                            icon="insurance"
                            @change="changeInsurance($event)"
                        ></select-input>
                    </div>
                </div>
                <div>
                    <tabs-field
                        :label="$t('forms.referrals.search.fields.search_by_time_window.label')"
                        :options="searchTimeSlotOptions"
                        :formData="data"
                        valuePath="searchByTimeWindow"
                        :size="isMobileLarge ? 'extra-small' : 'small'"
                        @change="onTimeframeChange"
                    ></tabs-field>
                </div>
                <common-divider>
                    <p>{{ $t('pages.auth.signup.steps.register.description.or') }}</p>
                </common-divider>
                <div class="calendar-wrapper">
                    <button type="button" :class="selectDateButtonClass" @click="selectDateOption">
                        {{ $t('pages.profile.professional.availability.pick_date') }}
                    </button>

                    <div v-if="showCustomCalendar" class="calendar-area" @click.stop>
                        <date-range-picker
                            ref="drp"
                            v-model="data.customDate"
                            opens="center"
                            :ranges="false"
                            :autoApply="true"
                            :singleDatePicker="true"
                            :minDate="minSelectableDate"
                            @update="onCustomDateChange($event)"
                        >
                            <template #input="picker">
                                <input class="custom-hidden-input" :value="picker.startDate" readonly />
                            </template>
                        </date-range-picker>
                    </div>

                    <p v-if="selectedDateText" class="selected-date-text">
                        {{ $t('pages.profile.professional.availability.date_selected') }}
                        <strong>{{ selectedDateText }}</strong>
                    </p>
                </div>
                <refer-outsider
                    v-if="searchTerm"
                    :name="searchTerm"
                    :isDisabled="!insuranceId"
                    @proceed-to-referral-creation="proceedToReferralCreation"
                ></refer-outsider>

                <common-buttons v-else>
                    <common-button
                        type="primary"
                        :isProcessing="isSending"
                        :label="$t('common.microcopy.verify_insurance')"
                        :isDisabled="!insuranceId || !selectedUser || !data.searchByTimeWindow"
                        size="large"
                        @click="submit"
                    ></common-button>
                </common-buttons>
            </common-content-spacer>
            <common-notification ref="commonSuccessNotification" :content="notificationMessage"></common-notification>
        </div>

        <invite-sent v-else-if="invitedUserEmail" :email="invitedUserEmail" @close="emitEvent({ name: 'close' })"></invite-sent>
    </div>
</template>

<script>
    import DateRangePicker from 'vue2-daterange-picker'
    import UsersAPI from 'api/UsersAPI'
    import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import SpecialitiesAPI from 'api/SpecialitiesAPI'
    import PracticesAPI from 'api/PracticesAPI'
    import Store from '~/store'
    import SelectInput from 'components/form-inputs/SelectInput'
    import CommonUserSearchAutocomplete from 'components/page-elements/search-autocomplete/CommonUserSearchAutocomplete.vue'
    import UserCard from 'components/page-elements/referrals/UserCard'
    import PracticeCard from 'components/page-elements/practice/PracticeCard'
    import AvailabilityLocations from 'components/page-elements/profile/AvailabilityLocations'
    import ReferOutsider from 'components/page-elements/referrals/ReferOutsider'
    import InviteSent from 'components/page-elements/invites/InviteSent'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import PracticePlanEnum from '@/enums/PracticePlanEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import TimeWindowSearchCriteriaEnum from '@/enums/TimeWindowSearchCriteriaEnum'
    import TabsField from '../../form-fields/TabsField.vue'
    import FormMixin from 'mixins/FormMixin'
    import dayjs from 'dayjs'

    export default {
        mixins: [ScreenSizeDetectionMixin, FormMixin],
        components: {
            CommonUserSearchAutocomplete,
            SelectInput,
            UserCard,
            PracticeCard,
            AvailabilityLocations,
            ReferOutsider,
            InviteSent,
            TabsField,
            DateRangePicker,
        },

        props: {
            user: { type: Object, default: null },
            insurancePlanId: { type: Number, default: null },
            existingReferral: { type: Object, required: false, default: null },
            activePracticeType: { type: String, default: PracticeTypeEnum.PHYSICIAN },
            showAsInnerModal: { type: Boolean, default: false },
            eprescribe: { type: Boolean, default: false },
            ehr: { type: Boolean, default: false },
            patientId: { type: Number, default: null, required: false },
            patientPrimaryInsuranceTypeId: { type: Number, default: null, required: false },
        },

        data() {
            return {
                insuranceId: null,
                insurance: null,
                selectedUser: null,
                npiPractices: [],
                practiceLookup: 0,
                showNotAcceptingInsurance: false,
                showAcceptingInsurance: false,
                practiceLocations: [],
                insurancePlans: [],
                isPageReady: false,
                searchTerm: null,
                invitedUserEmail: null,
                practiceTypeValue: PracticeTypeEnum.PHYSICIAN,
                PracticeTypeEnum: PracticeTypeEnum,
                PracticePlanEnum: PracticePlanEnum,
                maxNumberOfItems: null,
                notificationMessage: '',
                isSending: false,
                externalUser: {},
                showCustomCalendar: false,
                data: {
                    searchByTimeWindow: '',
                    customDate: {
                        startDate: null,
                        endDate: null,
                    },
                },
                isDateSelected: false,
                selectedDateText: '',
            }
        },

        computed: {
            practiceOptions() {
                return this.npiPractices.map((practice) => ({
                    value: practice.userId,
                    label: [practice.practiceName, [practice.city, practice.state].filter(Boolean).join(', ')]
                        .filter(Boolean)
                        .join(' · '),
                }))
            },
            minSelectableDate() {
                return dayjs().startOf('day').toDate()
            },
            selectDateButtonClass() {
                return {
                    'select-date-btn': true,
                    'select-date-btn--active': this.showCustomCalendar || this.isDateSelected,
                }
            },
            searchTimeSlotOptions() {
                return [...TimeWindowSearchCriteriaEnum]
            },
            practiceTypes() {
                return [PracticeTypeEnum.PHYSICIAN, PracticeTypeEnum.ANCILLARY].map((value) => ({
                    label: this.$t(`forms.auth.register.fields.practice_type.options.${value}.label`),
                    value,
                }))
            },

            showContent() {
                return this.showNotAcceptingInsurance
                    ? this.$t('modals.make_direct_referal_modal.content', {
                          user: this.selectedUser.name,
                          designation: this.selectedUser.designation,
                          insurance: this.insurance.name,
                      })
                    : ''
            },

            classList() {
                return {
                    'direct-referral': true,
                    'direct-referral__no-insurance': this.showNotAcceptingInsurance,
                }
            },

            searchLabel() {
                return this.practiceTypeValue === PracticeTypeEnum.PHYSICIAN
                    ? this.$t('modals.make_direct_referal_modal.physician')
                    : this.$t('modals.make_direct_referal_modal.ancillary')
            },

            searchPlaceholder() {
                return this.$t('modals.make_direct_referal_modal.start_typing')
            },

            getPageHeader() {
                if (this.showNotAcceptingInsurance) {
                    return ''
                }
                return this.practiceTypeValue === PracticeTypeEnum.PHYSICIAN
                    ? this.$t('modals.make_direct_referal_modal.refer_directly_physican')
                    : this.$t('modals.make_direct_referal_modal.refer_directly_ancillary')
            },
        },

        methods: {
            onCustomDateChange(event) {
                const selectedDate = event?.startDate

                if (!selectedDate) return

                this.change({
                    path: 'customDate',
                    value: event,
                })

                this.change({
                    path: 'searchByTimeWindow',
                    value: 'custom',
                })

                this.selectedDateText = dayjs(selectedDate).format('MMM DD, YYYY')

                this.showCustomCalendar = false
                this.isDateSelected = true
            },

            onTimeframeChange({ path, value }) {
                this.change({ path, value })
                this.isDateSelected = false
                this.showCustomCalendar = false
                this.selectedDateText = ''
                this.change({
                    path: 'customDate',
                    value: {
                        startDate: null,
                        endDate: null,
                    },
                })
            },
            selectDateOption() {
                this.change({
                    path: 'searchByTimeWindow',
                    value: 'custom',
                })
                this.showCustomCalendar = true
                this.isDateSelected = true
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.$refs.drp?.togglePicker(true)
                    }, 50)
                })
            },
            submit() {
                this.isSending = true
                this.practiceTypeValue === PracticeTypeEnum.PHYSICIAN
                    ? this.checkUserAcceptInsurance()
                    : this.checkPracticeAcceptInsurance()
            },

            matchReferral() {
                if (this.eprescribe) {
                    this.$emit('match-eprescribe')
                    return
                }

                this.$router.push({
                    name: 'referrals.new',
                    query: { insurancePlanId: this.insuranceId, practiceType: this.practiceTypeValue },
                })
            },

            checkPracticeAcceptInsurance() {
                PracticesAPI.checkPracticeAcceptingInsuranceType({
                    practiceId: this.selectedUser.id,
                    insuranceTypeId: this.insuranceId,
                }).then((res) => {
                    PracticesAPI.getPracticeLocations(this.selectedUser.id).then((locations) => {
                        this.isPageReady = true

                        this.practiceLocations = locations.filter((location) => location.isAvailabilitySet)
                        this.practiceLocations.forEach((item) => delete item.practiceName)
                        this.showNotAcceptingInsurance = !res.isAccepting
                        if (this.practiceLocations.length === 1 && !this.showNotAcceptingInsurance) {
                            if (this.eprescribe) {
                                const payload = {
                                    selectedUser: this.selectedUser,
                                    insuranceId: this.insuranceId,
                                    practiceLocationId: this.practiceLocations[0].id,
                                }
                                this.$emit('open-test-selection', payload)
                                return
                            }
                            if (!this.ehr) {
                                this.goToSchedule({ practiceLocationId: this.practiceLocations[0].id })
                            } else {
                                this.showAcceptingInsurance = res.isAccepting
                                this.insurance = res.insurancePlan
                                this.selectedUser = res.practice
                                this.selectedUser.specialities = []
                            }
                        } else {
                            this.showAcceptingInsurance = res.isAccepting
                            this.insurance = res.insurancePlan
                            this.selectedUser = res.practice
                            this.selectedUser.specialities = []
                        }
                    })
                })
            },

            checkUserAcceptInsurance() {
                UsersAPI.checkUserAcceptingInsuranceType({
                    userId: this.selectedUser?.id,
                    insuranceTypeId: this.insuranceId,
                }).then((res) => {
                    PracticesAPI.getUserLocations(this.selectedUser.id).then((locations) => {
                        this.isPageReady = true

                        this.practiceLocations = locations.filter((location) => location.isAvailabilitySet)
                        this.showNotAcceptingInsurance = !res.isAccepting

                        if (this.practiceLocations.length === 1 && !this.showNotAcceptingInsurance) {
                            if (this.eprescribe) {
                                const payload = {
                                    selectedUser: res.user,
                                    insuranceId: this.insuranceId,
                                    practiceLocationId: this.practiceLocations[0]?.id,
                                }
                                this.$emit('add-eprescribe', payload)
                                return
                            }
                            // EHR: never skip straight to schedule — let user confirm location in the modal.
                            if (!this.ehr) {
                                this.goToSchedule({ practiceLocationId: this.practiceLocations[0]?.id })
                            } else {
                                this.showAcceptingInsurance = res.isAccepting
                                this.insurance = res.insurancePlan
                                this.selectedUser = res.user
                            }
                        } else {
                            this.showAcceptingInsurance = res.isAccepting
                            this.insurance = res.insurancePlan
                            this.selectedUser = res.user
                        }
                    })
                })
            },

            inviteUserToSubscribe() {
                const data = {
                    toUserId: this.selectedUser.id,
                    ...(this.practiceTypeValue !== PracticeTypeEnum.PHYSICIAN && { referringTo: PracticeTypeEnum.ANCILLARY }),
                }
                UsersAPI.inivteUserToSubscribe(data).then(() => {
                    this.removeSearchTerm()
                    this.insuranceId = null
                    this.selectedUser = null
                    this.removeSearchTerm()
                    this.$refs.searchAC.removeSelectedUser()
                    this.$refs.searchAC.searchTerm = ''
                    this.notificationMessage = this.$t('modals.make_direct_referal_modal.inivte_billing.invite_success')
                    this.$refs.commonSuccessNotification.showNotification()
                })
            },

            changeInsurance(event) {
                this.insuranceId = event
            },

            userSelected(event) {
                this.npiPractices = []
                this.practiceLookup += 1
                if (event?.isRegistryProvider) {
                    this.selectedUser = null
                    this.loadNpiPractices(event, this.practiceLookup)
                    return
                }
                this.selectedUser = event
            },

            // The registry gives the NPI; MedMatch says which practices hold it. Several practices: the referrer picks,
            // and the practice routing would pick comes first. None: the existing refer-outside form takes over.
            async loadNpiPractices(provider, lookup) {
                let practices = []
                try {
                    const specialityId = await this.medMatchSpecialityId(provider.specialization)
                    const rows = await ReferralsPoolAPI.getNpiPractices({ npis: [provider.npi], specialityId })
                    const list = Array.isArray(rows) ? rows : rows?.data || []
                    practices = list.find((row) => String(row.npi) === provider.npi)?.practices || []
                } catch {
                    if (lookup !== this.practiceLookup) return
                    this.$refs.searchAC.removeSelectedUser()
                    this.notificationMessage = this.$t('modals.make_direct_referal_modal.practice_lookup_failed')
                    this.$refs.commonSuccessNotification.showNotification()
                    return
                }
                if (lookup !== this.practiceLookup) return // the referrer changed the provider meanwhile

                if (!practices.length) {
                    this.$refs.searchAC.removeSelectedUser()
                    this.setSearchTerm({ searchTerm: provider.name })
                    return
                }

                this.npiPractices = practices
                this.choosePractice(practices[0].userId)
            },

            // The registry's specialty ids differ from MedMatch's, so the name links them.
            async medMatchSpecialityId(specialization) {
                if (!specialization) return undefined
                const options = await SpecialitiesAPI.getList()
                const wanted = specialization.trim().toLowerCase()
                return options.find((option) => option.label?.trim().toLowerCase() === wanted)?.value
            },

            choosePractice(userId) {
                const practice = this.npiPractices.find((item) => item.userId === userId)
                if (!practice) return
                this.selectedUser = {
                    id: practice.userId,
                    firstName: practice.firstName,
                    lastName: practice.lastName,
                    practiceName: practice.practiceName,
                }
            },

            selectAgain() {
                this.showNotAcceptingInsurance = false
            },

            practiceTypeChange(event) {
                this.removeSearchTerm()
                this.insuranceId = null
                this.selectedUser = null
                this.removeSearchTerm()
                this.$refs.searchAC.removeSelectedUser()
                this.$refs.searchAC.searchTerm = ''
                this.practiceTypeValue = event
            },

            openTestsSelection({ practiceLocationId }) {
                const payload = {
                    selectedUser: this.selectedUser,
                    insuranceId: this.insuranceId,
                    practiceLocationId,
                }
                this.$emit('open-test-selection', payload)
            },

            goToSchedule({ practiceLocationId }) {
                if (this.practiceTypeValue === PracticeTypeEnum.PHYSICIAN) {
                    ReferralsAPI.getDataForDirectReferral(this.selectedUser.id).then((res) => {
                        const isCustom = this.data.searchByTimeWindow === 'custom'

                        const query = {
                            userId: res.referredBy,
                            practiceType: res.practice.practiceType,
                            searchBy: 'zipcode',
                            insurancePlanId: res.insurancePlanId || this.insuranceId,
                            specialityId: res.specialityId,
                            subSpecialityId: res.subSpecialityId,
                            zip: res.practice.zip,
                            physicianId: res.referredTo,
                            practiceId: res.practice.id,
                            practiceLocationId,
                            timeframe: this.data.searchByTimeWindow,
                            ...(isCustom && {
                                customDate: dayjs(this.data.customDate?.startDate).format('YYYY-MM-DD'),
                            }),
                            ...(this?.$route?.params?.id && { patientId: this.$route.params.id }),
                        }

                        if (this.ehr) {
                            this.$router.push({
                                name: `ehr.referrals.new.make`,
                                query: {
                                    ...query,
                                    key: this.$route.query.key,
                                    externalBooking: 'ehr',
                                },
                                params: {
                                    existingReferral: this.existingReferral,
                                },
                            })
                            return
                        }

                        this.$router.push({
                            name: `referrals.new.make`,
                            query: query,
                            params: {
                                existingReferral: this.existingReferral,
                            },
                        })
                    })
                } else {
                    const isCustom = this.data.searchByTimeWindow === 'custom'
                    const query = {
                        practiceLocationId,
                        practiceType: this.practiceTypeValue,
                        practiceId: this.selectedUser.id,
                        insurancePlanId: this.insuranceId,
                        practiceName: this.selectedUser.name,
                        searchBy: 'zipcode',
                        ...(this?.$route?.params?.id && { patientId: this.$route.params.id }),
                        timeframe: this.data.searchByTimeWindow,
                        ...(isCustom && {
                            customDate: dayjs(this.data.customDate?.startDate).format('YYYY-MM-DD'),
                        }),
                    }
                    if (this.ehr) {
                        this.$router.push({
                            name: `ehr.referrals.services.tests`,
                            query: {
                                ...query,
                                externalBooking: 'ehr',
                            },
                            params: {
                                existingReferral: this.existingReferral,
                            },
                        })
                        return
                    }
                    this.$router.push({
                        name: `referrals.services.tests`,
                        query: query,
                        params: {
                            existingReferral: this.existingReferral,
                        },
                    })
                }
                this.emitEvent({ name: 'location-selected' })
            },

            removeSearchTerm() {
                this.searchTerm = null
            },

            setSearchTerm({ searchTerm }) {
                this.searchTerm = searchTerm
            },

            inviteSent({ email }) {
                this.invitedUserEmail = email
            },

            setNumberOfItems() {
                if (this.isTablet) {
                    this.maxNumberOfItems = 1
                    return
                }
                if (this.isLaptop) {
                    this.maxNumberOfItems = 2
                    return
                }

                this.maxNumberOfItems = 3
            },

            proceedToReferralCreation(payload) {
                this.externalUser = {
                    firstName: payload.firstName,
                    lastName: payload.lastName,
                    email: payload.email,
                }

                if (this.practiceTypeValue === PracticeTypeEnum.ANCILLARY) {
                    this.$router.push({
                        name: `referrals.services.tests.outsider`,
                        query: {
                            insurancePlanId: this.insuranceId,
                            practiceType: this.practiceTypeValue,
                            outsider: true,
                            ...this.externalUser,
                            patientId: this.patientId,
                        },
                    })
                    return
                }

                this.$router.push({
                    name: `referrals.new.make`,
                    query: {
                        insurancePlanId: this.insuranceId,
                        practiceType: this.practiceTypeValue,
                        outsider: true,
                        ...this.externalUser,
                        patientId: this.patientId,
                    },
                })
            },
        },

        watch: {
            windowWidth() {
                this.setNumberOfItems()
            },
        },

        mounted() {
            this.setNumberOfItems()
            if (this.activePracticeType) {
                this.practiceTypeValue = this.activePracticeType
            }
            this.insurancePlans = Store.getters['auth/getInsurancePlans']

            this.selectedUser = this.user
            this.insuranceId = this.insurancePlanId || this.patientPrimaryInsuranceTypeId
            // EHR: do not auto-run verify — user must click "Verify insurance" first.
            // Otherwise pre-filled user + insurance immediately submits and can auto-navigate to schedule.
            if (this.selectedUser && this.insuranceId && !this.ehr) {
                this.submit()
            } else {
                this.isPageReady = true
            }
        },
    }
</script>
<style scoped lang="scss">
    @import 'styles/components/referrals/_app-direct-referral';
    .refer-direct-container {
        margin-left: 40px;
        margin-right: 40px;
    }
    .calendar-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-bottom: 80px;
    }

    .selected-date-text {
        margin-top: 12px;
        font-size: 14px;
        font-weight: 500;
    }
    .custom-hidden-input {
        position: absolute;
        opacity: 0;
        pointer-events: none;
        width: 1px;
        height: 1px;
    }

    .calendar-area {
        position: absolute;
        top: 10px;
        z-index: 9999;
    }
    .select-date-btn {
        user-select: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        width: 100%;
        max-width: rem(224px);

        margin: 0 auto;

        border: rem(1px) solid $color--border-switch;
        background: $color--bg-input;

        border-radius: rem($border-radius--base);

        transition: trans(background), trans(border), trans(color);

        padding: rem(15px);
        height: rem(50px);

        font-size: rem($font-size--form);
        line-height: rem(20px);

        color: inherit;

        &:hover:not(.select-date-btn--active) {
            background: #fff;
        }

        &:focus {
            outline: none;
        }

        &--active {
            background: $color--accent-1;
            color: #fff;
            border-color: $color--accent-1;
            z-index: $layer--above-parent;

            &:hover {
                background: $color--accent-1;
                border-color: $color--accent-1;
            }
        }
    }
</style>
